import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { CustomerProfile, Reward, OfferTypeFilter } from '@/models';
import { CountryCode } from '@/store/ui';
import { shouldShowRewards } from '@/helpers/country';
import { Routes } from '@/router';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';

@Component({
  name: 'CmgAccountModalRewardsStore',
})
export default class CmgAccountModalRewardsStore extends Vue {
  mounted() {
    this.$services.reward.getRewardOffers();
    this.$services.reward.getExtrasCampaigns();
    this.$services.featureFlags.refreshApimFeatureFlags();
  }

  get isServiceOnline(): boolean {
    return !this.$appStore.state.reward.unreachable;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }

  get isRewardsExchangeOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsExchangeOnline ?? true;
  }

  get offlineMessage(): string {
    return `We're having some trouble getting your points.`;
  }

  get medallionRewardShout(): string {
    return this.$appStore.state.cms.images.logos.medallionRewardShout.default;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get temporaryCountryMode(): boolean {
    return !!this.customer && this.profileCountryCode !== this.siteCountryCode;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get profileCountryCode(): string {
    return this.customer ? this.customer.country || CountryCode.US : '';
  }

  get siteCountryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get shouldShowRewards(): boolean {
    return shouldShowRewards(this.countryCode);
  }

  get pointsBalance(): number {
    return this.$services.reward.getPointsBalance();
  }

  get userIsGuacMode(): boolean {
    return this.$appStore.getters.isCustomerGuacMode;
  }

  get guacModeSmallImageUrl(): string {
    return this.$appStore.state.cms.images.icons.guacModeSmall.default;
  }

  get favoritedReward(): Reward | undefined {
    return this.$services.reward.getFavoriteReward();
  }

  get pointsNeeded(): number {
    if (this.favoritedReward && this.favoritedReward.points > this.pointsBalance) {
      return this.favoritedReward.points - this.pointsBalance;
    }

    return 0;
  }

  get rewards(): Reward[] {
    let rewards = this.$appStore.state.reward.rewards
      .filter((reward: Reward) => reward.offerType == OfferTypeFilter.Food && reward.points <= this.pointsBalance)
      .sort((a: Reward, b: Reward) => a.sortOrder - b.sortOrder);

    if (rewards.length > 2) {
      rewards = rewards.slice(0, 2);
    }

    return rewards;
  }

  get numRewards(): number {
    return this.$services.reward.getNumRedeemableRewards();
  }

  get numPromotions(): number {
    return this.$appStore.state.customer.wallet.promotions.length;
  }

  get numCampaigns(): number {
    return this.$appStore.state.reward.campaigns.length;
  }

  get sadAvocado(): string {
    return this.$appStore.state.cms.images.feedback.sadAvocado.default;
  }

  get pepperMedallionOutlinedImageUrl(): string {
    return this.$appStore.state.cms.images.logos.medallionPepperOutlined.default;
  }

  get bagMedallionOutlinedImageUrl(): string {
    return this.$appStore.state.cms.images.logos.medallionBagOutlined.default;
  }

  get extrasMedallionOutlinedImageUrl(): string {
    return this.$appStore.state.cms.images.logos.medallionExtrasOutlined.default;
  }

  get isEmployeeAccount(): boolean {
    if (!this.customer) {
      return false;
    }
    
    if (this.customer.isEmployee) {
      this.$services.config.setEmployeeRewardsHowItWorksModal();
    }
    return this.customer.isEmployee;
  }

  get employeeVerifiedBadge(): string {
    return this.$appStore.state.cms.images.logos.badgeEmployeeVerified.default;
  }

  onClickRewardsExchange() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsAccount,
      action: AnalyticsEventAction.RewardExchangeClick,
      label: AnalyticsEventLabel.RewardsRewardExchangeClick,
    });
    this.$router.push({ name: Routes.RewardsExchange });
    this.close();
  }

  onClickRewardsHome() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsAccount,
      action: AnalyticsEventAction.RewardBonusClick,
      label: AnalyticsEventLabel.RewardBonusClick,
    });
    this.$router.push({ name: Routes.Rewards });
    this.close();
  }

  onClickRewardsExtras() {
    this.$router.push({ name: Routes.RewardsExtras });
    this.close();
  }

  onClickScanReminder() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsAccount,
      action: AnalyticsEventAction.ForgotToScanClick,
      label: AnalyticsEventLabel.AccountMenuForgotToScanClick,
    });
    this.$router.push({ name: Routes.RetroCredit });
    this.close();
  }

  onClickViewPointsHistory() {
    this.$router.push({ name: Routes.RewardsPointsHistory });
    this.close();
  }

  onClickGuacMode(){
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsAccount,
      action: AnalyticsEventAction.GuacModeClick,
      label: AnalyticsEventLabel.GuacModeClick,
    });
  }

  onClickEmployeeVerifiedBadge() {
    this.$services.ui.setModalVisibility('employeeRewardsHowItWorksModal', true);
  }

  close() {
    this.$services.ui.setModalVisibility('account', false);
    this.$services.ui.focusBack();
  }
}
